import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { trackPageView as posthogPageView } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = 'dl_session_id';

const getSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Skip tracking for admin and login routes
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/Penelopesims') || location.pathname === '/login') return;

    const path = location.pathname;
    const storyMatch = path.match(/^\/stories\/(.+)$/);
    const storySlug = storyMatch ? storyMatch[1] : null;

    // Fire and forget - don't block UI
    axios.post(`${API}/track/pageview`, {
      page_path: path,
      story_slug: storySlug,
      session_id: getSessionId(),
      referrer: document.referrer || null
    }).catch(() => {
      // Silent fail - tracking shouldn't break the app
    });

    // PostHog pageview (SPA-safe — manual fire on every route change).
    posthogPageView(path);
  }, [location.pathname, location.search]);

  // iter256 · Real-user speed beacon — one sample per full document load
  // with navigation timings. Powers the admin Speed dashboard.
  useEffect(() => {
    const send = () => {
      if (window.__dlPerfSent) return;
      window.__dlPerfSent = true;
      try {
        const path = window.location.pathname;
        if (path.startsWith('/admin') || path.startsWith('/Penelopesims') || path === '/login') return;
        const n = performance.getEntriesByType('navigation')[0];
        if (!n || !n.responseStart) return;
        const fcpE = performance.getEntriesByType('paint').find((p) => p.name === 'first-contentful-paint');
        const KNOWN = new Set(['network', 'search', 'contact', 'brands', 'schools', 'stories', 'flyt', 'pulse', 'saved',
          'for-athletes', 'for-agents', 'for-brands', 'for-programs', 'school-partnerships', 'visionaries', 'builders']);
        const seg = path.split('/').filter(Boolean);
        const route = seg.length === 0 ? '/'
          : KNOWN.has(seg[0]) ? (seg.length > 1 ? `/${seg[0]}/*` : `/${seg[0]}`)
          : seg[0] === 'l' || seg[0] === 'brand' ? `/${seg[0]}/*`
          : '/profile/*';
        axios.post(`${API}/track/perf`, {
          route,
          ttfb: Math.round(n.responseStart),
          fcp: fcpE ? Math.round(fcpE.startTime) : null,
          dcl: Math.round(n.domContentLoadedEventEnd),
          load: Math.round(n.loadEventEnd) || null,
          mobile: /Mobi|Android|iPhone/i.test(navigator.userAgent),
        }).catch(() => {});
      } catch (_) { /* telemetry must never break the app */ }
    };
    if (document.readyState === 'complete') {
      const t = setTimeout(send, 1200);
      return () => clearTimeout(t);
    }
    window.addEventListener('load', () => setTimeout(send, 1200), { once: true });
    return undefined;
  }, []);
};

export default usePageTracking;
